import { useQueryClient } from '@tanstack/react-query';
import { FilterIcon } from 'lucide-react';
import { useEffect, useState } from 'react';

import { Dropdown, SectionBreakCurve } from '@/components';
import { useAnalytics } from '@/lib/analytics';
import { useBatchSelectionDrawerStore } from '@/stores/batch-selection-drawer';

import { useBatchData } from '../../hooks/use-batch-data';
import { useBatchFiltering } from '../../hooks/use-batch-filtering';
import { useBatchSelection } from '../../hooks/use-batch-selection';
import { useConfirmModal } from '../../hooks/use-confirm-modal';
import type { BatchSelectionProps } from '../../types';
import { getDayAbbreviation } from '../../utils';
import { BatchDayFilter, BatchDayFilterTrigger } from '../batch-day-filter';
import { BatchFilterButton } from '../batch-filter-button';
import { BatchesList } from '../batches-list';
import { ConfirmBatchCard } from '../confirm-batch-card';
import { ConfirmBatchModal } from '../confirm-batch-modal';

import { TimezoneWithFlag } from './timezone-with-flag';

const BatchSelection = ({
  onboardingCourses,
  profileId,
  leadId,
  variantIds,
  country,
  userEmail,
  selectedProfile,
}: BatchSelectionProps) => {
  const { trackEvent } = useAnalytics();
  const setIsDrawerOpen = useBatchSelectionDrawerStore(
    state => state.setIsDrawerOpen
  );
  const {
    processedBatchesData,
    dayCombinations,
    isLoading,
    hasError,
    loadingVariants,
    errorVariants,
    successVariants,
    progress,
    refetch,
    getTagDetails,
    voucherLabel,
  } = useBatchData({
    profileId,
    variantIds,
    country,
  });

  const queryClient = useQueryClient();

  const {
    // selectedFilter,
    selectedDays,
    filteredBatchesData,
    // setSelectedFilter,
    handleDayFilterApply,
    handleDayFilterClear,
  } = useBatchFiltering({
    processedBatchesData,
  });

  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [showWaitlistReasonDropdown, setShowWaitlistReasonDropdown] =
    useState(false);
  const [selectedWaitlistReason, setSelectedWaitlistReason] = useState('');

  const { selectedBatches, handleBatchSelection, handleWaitlistSelection } =
    useBatchSelection({
      processedBatchesData,
      onAllSelected: () => setIsConfirmModalOpen(true),
    });

  const {
    selectedBatchDetails,
    isAssigning,
    handleConfirm: confirmBatches,
  } = useConfirmModal({
    getSelectedBatches: () => selectedBatches,
    processedBatchesData,
    profileId,
    leadId,
    variantIds,
    country,
  });

  useEffect(() => {
    const allSelectedWaitlistBatches =
      selectedBatchDetails.find(batch => batch?.isWaitlist) ?? null;
    if (allSelectedWaitlistBatches) {
      setShowWaitlistReasonDropdown(true);
    } else {
      setShowWaitlistReasonDropdown(false);
    }
  }, [selectedBatchDetails]);

  const handleConfirm = async () => {
    trackEvent('BatchConfirmed', 1, {
      courseNames:
        selectedBatchDetails?.map(detail => detail?.courseName)?.join(', ') ??
        '',
      selectedWaitlistReason,
    });
    await confirmBatches(selectedWaitlistReason);
    setIsConfirmModalOpen(false);
    setIsDrawerOpen(false);
    void queryClient.invalidateQueries();
  };

  // Show loading state with progress
  if (isLoading) {
    return (
      <div className="flex flex-col items-center">
        <div className="flex items-center bg-purplish-blue-light-cardbg p-7 w-full">
          <h1 className="font-bold text-3xl text-magical-moonlight-d4 capitalize mr-4">
            Select batch
          </h1>
        </div>
        <div className="flex flex-col items-center justify-center h-64 gap-4">
          <div className="text-magical-moonlight-d3">
            {loadingVariants.length > 0
              ? `Loading ${loadingVariants.map(v => v.courseName).join(', ')} batches...`
              : 'Loading available batches...'}
          </div>
          <div className="text-sm text-magical-moonlight-d2">
            Progress: {progress.completed}/{progress.total} (
            {progress.percentage}%)
          </div>
        </div>
      </div>
    );
  }

  // Show error state with retry option
  if (hasError && successVariants.length === 0) {
    return (
      <div className="flex flex-col items-center">
        <div className="flex items-center bg-purplish-blue-light-cardbg p-7 w-full">
          <h1 className="font-bold text-3xl text-magical-moonlight-d4 capitalize mr-4">
            Select batch
          </h1>
        </div>
        <div className="flex flex-col items-center justify-center h-64 gap-4">
          <div className="text-red-500">
            {errorVariants.length > 0
              ? `Error loading ${errorVariants.map(v => v.courseName).join(', ')} batches.`
              : 'Error loading batches.'}
          </div>
          <button
            onClick={refetch}
            className="px-4 py-2 bg-nirvana-jewel text-white rounded hover:bg-nirvana-jewel-dark"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center">
      {/* Header */}
      <div className="bg-purplish-blue-light-cardbg w-full">
        <div className="max-w-(--max-width) lg:px-16 md:px-8 px-4 mx-auto">
          <div className="flex items-center p-7">
            <h1 className="font-bold text-sm md:text-3xl text-magical-moonlight-d4 capitalize mr-0 md:mr-4">
              Select batch
            </h1>
            <TimezoneWithFlag timezone={selectedProfile?.timezone} />
          </div>
        </div>
        <SectionBreakCurve
          topColor="var(--color-purplish-blue-light-cardbg)"
          bottomColor="var(--color-magical-moonlight-white)"
        />
      </div>

      <div className="w-full px-0 mb-20">
        <div className="flex flex-row items-center gap-3 p-4 px-4 md:px-10 w-full md:max-w-(--max-width) mx-auto">
          <BatchDayFilterTrigger
            side="bottom"
            align="end"
            onApply={handleDayFilterApply}
            onClear={handleDayFilterClear}
            selectedDays={selectedDays}
            dayCombinations={dayCombinations}
            batchDayFilter={<BatchDayFilter />}
            className="ml-auto"
          >
            <BatchFilterButton
              showDot={false}
              selected={selectedDays.length > 0}
            >
              <div className="flex items-center gap-2">
                <FilterIcon className="w-5 h-5" />
                <span>
                  {selectedDays.length > 0
                    ? selectedDays
                        .map(day => getDayAbbreviation(day))
                        .join(' - ')
                    : 'Filter by Day'}
                </span>
              </div>
            </BatchFilterButton>
          </BatchDayFilterTrigger>
        </div>
        {/* Batch cards */}
        <div className="flex sm:flex-col flex-row w-full sm:gap-6 gap-0 overflow-x-scroll">
          {Object.keys(filteredBatchesData).map((courseName, index) => (
            <div key={courseName} className="flex-shrink-0 w-[85%] sm:w-full">
              <div className="lg:flex hidden">
                {index % 2 == 1 && (
                  <SectionBreakCurve
                    topColor="var(--color-magical-moonlight-white)"
                    bottomColor="var(--color-purplish-blue-light-cardbg)"
                  />
                )}
              </div>
              <BatchesList
                selectedCourse={onboardingCourses?.find(
                  course => course.course_name === courseName
                )}
                courseName={courseName}
                courseData={filteredBatchesData[courseName]}
                index={index}
                selectedBatches={selectedBatches}
                onBatchSelection={handleBatchSelection}
                onWaitlistSelection={handleWaitlistSelection}
                getTagDetails={getTagDetails}
                selectedDays={selectedDays}
                batchAssignStatus={processedBatchesData[courseName]?.status}
                timezone={selectedProfile?.timezone}
              />
              <div className="lg:flex hidden">
                {index % 2 == 1 && (
                  <SectionBreakCurve
                    topColor="var(--color-purplish-blue-light-cardbg)"
                    bottomColor="var(--color-magical-moonlight-white)"
                  />
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Confirm Batch Modal */}
        <ConfirmBatchModal
          isOpen={isConfirmModalOpen}
          onClose={() => setIsConfirmModalOpen(false)}
          onConfirm={() => void handleConfirm()}
          isLoading={isAssigning}
          selectedProfile={selectedProfile}
          doesSelectionIncludesWaitlist={showWaitlistReasonDropdown}
          selectedWaitlistReason={selectedWaitlistReason}
          totalBatchCount={selectedBatchDetails.filter(Boolean).length}
          voucherLabel={voucherLabel}
        >
          {selectedBatchDetails.map((detail, index) => {
            const cardBgColors = [
              'bg-blue-chaos-light-cardbg!',
              'bg-purplish-blue-light-cardbg!',
            ];
            const alternatingBg =
              selectedBatchDetails.length > 1
                ? cardBgColors[index % cardBgColors.length]
                : '';
            return (
              <ConfirmBatchCard
                key={detail!.courseName}
                batchName={
                  detail!.isWaitlist
                    ? `${detail!.courseName} Waitlist`
                    : `${detail!.courseName} Batch`
                }
                batchCode={
                  detail!.isWaitlist
                    ? `Waitlisted`
                    : (detail!.batchName ?? 'Batch')
                }
                scheduleDetails={detail!.scheduleDetails}
                startDay={detail!.startDay}
                startDate={detail!.startDate}
                email={userEmail ?? ''}
                timezone={selectedProfile?.timezone}
                isStartingSoon={
                  !(detail!.batch?.tags?.includes('Ongoing') ?? false)
                }
                isOngoing={detail!.batch?.tags?.includes('Ongoing') ?? false}
                completedSessions={detail!.batch?.completedSessionCount ?? 0}
                className={alternatingBg}
                theme={(() => {
                  if (detail!.isWaitlist) return 'waitlist';
                  return detail!.courseName.toLowerCase() === 'math'
                    ? 'math'
                    : 'english';
                })()}
              />
            );
          })}
          {showWaitlistReasonDropdown ? (
            <div className="my-2">
              <Dropdown
                id="batch-select"
                value={selectedWaitlistReason}
                onValueChange={value => {
                  setSelectedWaitlistReason(value);
                }}
                placeholder={`Select your reason to join waitlist*`}
                options={[
                  {
                    label: `I don't like any of the batch listed here`,
                    value: `I don't like any of the batch listed here`,
                  },
                  {
                    label: `I don't want to start right way`,
                    value: `I don't want to start right way`,
                  },
                  {
                    label: `There are no batches available`,
                    value: `There are no batches available`,
                  },
                ]}
                size={'default'}
              />
            </div>
          ) : null}
        </ConfirmBatchModal>
      </div>
    </div>
  );
};

export default BatchSelection;
